<script lang="ts" setup>
import { useCatalogStore } from '~/layers/catalog/store/catalog'
import { useCatalogGridStore } from '~/layers/catalog/store/catalogGrid'

type TFilterItem = {
  code: string
  optionCode?: string
  payload: TFilterStoring
  title: string
}

const props = defineProps({
  type: {
    type: String as PropType<CatalogDataProviderType>,
    required: true,
  },
})

const store = useCatalogStore()
const gridStore = useCatalogGridStore()
const service = useCatalogFilter()
const bus = useCatalogEventBus()
const globalBus = useAppEventBus()

const createTitle = (str: string, code: string): string => {
  const prefixDict =
    props.type === 'jewelry' ? jewerlyPrefixDict : diamondPrefixDict
  const prefix = prefixDict[code] || ''
  const suffix = filterSuffixDict[code] || ''
  return `${prefix} ${str} ${suffix}`.trim()
}

const applied = computed(() => {
  const appliedOptions = store.filters
  const result = [] as TFilterItem[]
  for (const filter of appliedOptions) {
    if ('range' in filter.payload) {
      const from = getMaskedRangeValue(filter.payload.range.from)
      const to = getMaskedRangeValue(filter.payload.range.to)
      const rawFrom = from ? `ОТ ${from}` : null
      const rawTo = to ? `ДО ${to}` : null
      const rawName = [rawFrom, rawTo].filter(Boolean).join(' ')
      const title = createTitle(rawName, filter.code)
      if (!title) continue
      result.push({
        code: filter.code,
        title,
        payload: filter.payload,
      })
      continue
    }

    if ('options' in filter.payload && Array.isArray(filter.payload.options)) {
      const code = filter.code.replace('_rf', '')
      filter.payload.options.forEach((item) => {
        const availableFilter = gridStore.filters.find((af) => af.code === code)
        if (availableFilter && 'groups' in availableFilter) {
          availableFilter.groups.forEach((group) => {
            if (group && 'items' in group) {
              const element = group.items?.find((v) => v.code === item)
              if (element) {
                const title = createTitle(element.name, code)
                result.push({
                  code: code,
                  title,
                  payload: filter.payload,
                  optionCode: item,
                })
              }
            }
          })
        }
        if (availableFilter && 'items' in availableFilter) {
          const filterItem = availableFilter.items?.find((v) => v.code === item)
          if (filterItem) {
            const title = createTitle(filterItem?.name, availableFilter.code)
            result.push({
              code: filter.code,
              title,
              payload: filter.payload,
              optionCode: item,
            })
          }
        }
      })
      continue
    }
  }

  return result
})

const emitUpdated = () => {
  bus.emit('filter:updated')
  globalBus.emit('catalog:filterApplied')
}

const remove = (option: TFilterItem) => {
  if ('range' in option.payload) {
    service.remove(option.code)
  }
  if ('options' in option.payload) {
    service.set(option.code, {
      type: option.payload.type,
      options: option.payload.options.filter((o) => o !== option.optionCode),
    })
  }

  if (useMediaQuery().matches(['xs', 'sm'])) {
    emitUpdated()
  }
}

const categoryMap = computed(() => {
  const map = new Map(store.categories.map((c) => [c.code, c.name]))
  const categoriesFilter = gridStore.filters.find(
    (filter) => filter.code === 'categories',
  )

  if (categoriesFilter && 'items' in categoriesFilter) {
    for (const item of categoriesFilter.items) {
      if (item.code && !map.has(item.code)) {
        map.set(item.code, item.name)
      }
    }
  }

  return Object.fromEntries(map)
})

const selectedCategories = computed(() =>
  store.selectedCategories
    .map((code) => ({ code, title: categoryMap.value[code] }))
    .filter((item): item is { code: string; title: string } =>
      Boolean(item.title),
    ),
)
</script>

<template>
  <div
    class="catalog__labels"
    :class="{
      'catalog__labels--empty':
        selectedCategories.length === 0 && applied.length === 0,
    }"
  >
    <div
      v-for="category in selectedCategories"
      :key="`category-${category.code}`"
      class="catalog__label"
    >
      <span class="catalog__label-span">{{ category.title }}</span>
      <div
        class="catalog__label-del"
        @click="service.removeCategory(category.code)"
      >
        <svg-icon
          name="icon-close"
          width="20"
          height="20"
        />
      </div>
    </div>
    <div
      v-for="option in applied"
      :key="option.code + (option.optionCode || '')"
      class="catalog__label"
    >
      <span class="catalog__label-span">{{ option.title }}</span>
      <div
        class="catalog__label-del"
        @click="remove(option)"
      >
        <svg-icon
          name="icon-close"
          width="20"
          height="20"
        />
      </div>
    </div>
  </div>
</template>

<style></style>
